import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import {
  FaWrench,
  FaCarCrash,
  FaAmbulance,
  FaGasPump,
  FaExclamationTriangle,
  FaCar,
  FaShieldAlt,
  FaFire,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaGlobe,
  FaClock
} from 'react-icons/fa';
import { MdTireRepair, MdOutlineSos } from 'react-icons/md';
import { GiPoliceOfficerHead, GiTowTruck } from 'react-icons/gi';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import '../css/SOSModal.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const SOS_TYPES = [
  {
    id: 'breakdown',
    label: 'Lerobbantam',
    icon: FaWrench,
    osmQueries: [
      'shop=car_repair',
      'amenity=car_repair',
      'craft=car_repair',
      'service:vehicle:repair=yes'
    ],
    radius: 15000 // 15km
  },
  {
    id: 'flat_tire',
    label: 'Defekt',
    icon: MdTireRepair,
    osmQueries: [
      'shop=tyres',
      'service:vehicle:tyres=yes'
    ],
    radius: 10000 // 10km
  },
  {
    id: 'accident',
    label: 'Baleset',
    icon: FaCarCrash,
    emergencyNumbers: true, // Special flag for emergency numbers only
    numbers: [
      { name: 'Egységes Segélyhívó', number: '112', icon: FaExclamationTriangle, description: 'Minden vészhelyzet esetén' },
      { name: 'Mentők', number: '104', icon: FaAmbulance, description: 'Orvosi segítség' },
      { name: 'Rendőrség', number: '107', icon: GiPoliceOfficerHead, description: 'Baleseti helyszín biztosítása' },
      { name: 'Tűzoltóság', number: '105', icon: FaFire, description: 'Tűz vagy műszaki mentés' }
    ]
  },
  {
    id: 'fuel',
    label: 'Üzemanyag elfogyott',
    icon: FaGasPump,
    osmQueries: [
      'amenity=fuel'
    ],
    radius: 10000 // 10km
  },
  {
    id: 'towing',
    label: 'Szükségem van autómentőre',
    icon: GiTowTruck,
    showBothNumbersAndMap: true, // Special flag: show both emergency numbers AND map
    numbers: [
      { name: 'Útinform (KRESZ Info)', number: '06-1-336-2400', icon: GiTowTruck, description: 'Útinform autómentő szervezés', website: 'https://www.utinform.hu' },
      { name: 'Magyar Autóklub (MAK)', number: '06-1-345-1800', icon: FaCar, description: '24 órás autómentés', website: 'https://www.autoklub.hu' },
      { name: 'Europ Assistance', number: '06-1-460-1400', icon: FaShieldAlt, description: 'Nemzetközi autómentés', website: 'https://www.europ-assistance.hu' },
      { name: 'Generali Assistance', number: '06-1-451-6000', icon: FaWrench, description: 'Biztosítói autómentés', website: 'https://www.generali.hu' }
    ],
    osmQueries: [
      'service:vehicle:towing=yes',
      'amenity=car_repair',
      'shop=car_repair'
    ],
    radius: 20000 // 20km
  }
];

// Custom red icon for user location
const userIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="14" fill="#e32f20" stroke="white" stroke-width="3"/>
      <circle cx="16" cy="16" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

// Custom blue icon for service locations (classic map marker shape)
const serviceIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44">
      <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 28 16 28s16-16 16-28C32 7.163 24.837 0 16 0z" fill="#2563eb" stroke="white" stroke-width="2"/>
      <circle cx="16" cy="16" r="6" fill="white"/>
    </svg>
  `),
  iconSize: [32, 44],
  iconAnchor: [16, 44],
  popupAnchor: [0, -44]
});

const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
};

const SOSModal = ({ open, onClose }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    if (open) {
      // Get user location when modal opens
      if (navigator.geolocation) {
        setLoading(true);
        setLocationError(null);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            setLoading(false);
          },
          (error) => {
            console.error('Error getting location:', error);
            setLocationError('Nem sikerült megszerezni a helyzetedet. Kérlek, engedélyezd a helymeghatározást.');
            setLoading(false);
            // Default to Budapest center
            setUserLocation({ lat: 47.4979, lng: 19.0402 });
          }
        );
      } else {
        setLocationError('A böngésző nem támogatja a helymeghatározást.');
        // Default to Budapest center
        setUserLocation({ lat: 47.4979, lng: 19.0402 });
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedType && userLocation) {
      const sosType = SOS_TYPES.find(t => t.id === selectedType);
      // Fetch map data if it has osmQueries (skip only for pure emergency numbers like baleset)
      if (sosType.osmQueries) {
        fetchNearbyPlaces();
      }
    }
  }, [selectedType, userLocation]);

  const fetchNearbyPlaces = async () => {
    if (!selectedType || !userLocation) return;

    setLoading(true);
    const sosType = SOS_TYPES.find(t => t.id === selectedType);

    // Skip if no OSM queries defined
    if (!sosType.osmQueries) {
      setLoading(false);
      return;
    }

    try {
      // Build Overpass API query with OR logic for multiple tag options
      const queryParts = sosType.osmQueries.map(tagString => {
        const [key, value] = tagString.split('=');
        return `
          node["${key}"="${value}"](around:${sosType.radius},${userLocation.lat},${userLocation.lng});
          way["${key}"="${value}"](around:${sosType.radius},${userLocation.lat},${userLocation.lng});
        `;
      }).join('');

      const query = `
        [out:json][timeout:25];
        (
          ${queryParts}
        );
        out center;
      `;


      const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query
      });

      const data = await response.json();

      const places = data.elements.map(element => {
        const lat = element.lat || element.center?.lat;
        const lon = element.lon || element.center?.lon;

        return {
          id: element.id,
          name: element.tags?.name || 'Név nélkül',
          lat,
          lng: lon,
          address: formatAddress(element.tags),
          phone: element.tags?.phone || element.tags?.['contact:phone'],
          website: element.tags?.website || element.tags?.['contact:website'],
          opening_hours: formatOpeningHours(element.tags?.opening_hours),
          distance: calculateDistance(userLocation.lat, userLocation.lng, lat, lon)
        };
      }).filter(place => place.lat && place.lng)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 10); // Limit to 10 closest places

      setNearbyPlaces(places);
    } catch (error) {
      console.error('Error fetching places:', error);
      setNearbyPlaces([]);
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (tags) => {
    const parts = [];
    if (tags['addr:street']) parts.push(tags['addr:street']);
    if (tags['addr:housenumber']) parts.push(tags['addr:housenumber']);
    if (tags['addr:city']) parts.push(tags['addr:city']);
    if (tags['addr:postcode']) parts.push(tags['addr:postcode']);
    return parts.length > 0 ? parts.join(', ') : 'Cím nincs megadva';
  };

  const formatOpeningHours = (hours) => {
    if (!hours) return null;

    // Convert common OSM opening hours format to Hungarian
    let formatted = hours
      .replace(/Mo-Fr/g, 'Hétfő-Péntek')
      .replace(/Mo/g, 'Hétfő')
      .replace(/Tu/g, 'Kedd')
      .replace(/We/g, 'Szerda')
      .replace(/Th/g, 'Csütörtök')
      .replace(/Fr/g, 'Péntek')
      .replace(/Sa/g, 'Szombat')
      .replace(/Su/g, 'Vasárnap')
      .replace(/Sa-Su/g, 'Hétvége')
      .replace(/off/g, 'zárva');

    return formatted;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return (R * c).toFixed(1);
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleBack = () => {
    setSelectedType(null);
    setNearbyPlaces([]);
  };

  if (!open) return null;

  return (
    <div className="sos-modal-backdrop" onClick={() => {
      setSelectedType(null);
      setNearbyPlaces([]);
      onClose();
    }}>
      <div className="sos-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="sos-modal-header">
          <h2><MdOutlineSos /> Segítségkérés</h2>
          <button className="sos-modal-close" onClick={() => {
            // Modal bezárása és állapot resetelése
            setSelectedType(null);
            setNearbyPlaces([]);
            onClose();
          }}>×</button>
        </div>

        <div className="sos-modal-body">
          {!selectedType ? (
            <>
              <p className="sos-modal-description">
                Válaszd ki, hogy milyen típusú segítségre van szükséged:
              </p>

              {locationError && (
                <div className="sos-location-error">
                  <strong>⚠️ Figyelem:</strong> {locationError}
                </div>
              )}

              <div className="sos-types-grid">
                {SOS_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      className="sos-type-button"
                      onClick={() => handleTypeSelect(type.id)}
                      disabled={loading}
                    >
                      <span className="sos-type-icon"><Icon /></span>
                      <span className="sos-type-label">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <button className="sos-back-button" onClick={handleBack}>
                ← Vissza
              </button>

              <div className="sos-selected-type">
                {(() => {
                  const Icon = SOS_TYPES.find(t => t.id === selectedType)?.icon;
                  return Icon ? <span className="sos-type-icon-large"><Icon /></span> : null;
                })()}
                <h3>{SOS_TYPES.find(t => t.id === selectedType)?.label}</h3>
              </div>

              {/* Show emergency numbers if type has them (baleset or towing) */}
              {SOS_TYPES.find(t => t.id === selectedType)?.numbers && (
                <div className="sos-emergency-numbers">
                  <div className="sos-emergency-warning">
                    {selectedType === 'accident' ? (
                      <><FaExclamationTriangle /> <strong>Sürgős segítségkérés</strong><br />Hívd azonnal az alábbi segélyhívó számok egyikét!</>
                    ) : (
                      <><FaPhoneAlt /> <strong>Országos autómentő cégek</strong><br />24 órás segítség az alábbi cégeknél:</>
                    )}
                  </div>
                  {SOS_TYPES.find(t => t.id === selectedType)?.numbers.map((emergency, index) => {
                    const Icon = emergency.icon;
                    return (
                      <div key={index} className="sos-emergency-card">
                        <div className="sos-emergency-icon"><Icon /></div>
                        <div className="sos-emergency-info">
                          <strong>{emergency.name}</strong>
                          <div className="sos-emergency-description">{emergency.description}</div>
                          {emergency.website && (
                            <div className="sos-emergency-website">
                              <FaGlobe /> <a href={emergency.website} target="_blank" rel="noopener noreferrer">
                                Weboldal
                              </a>
                            </div>
                          )}
                        </div>
                        <a href={`tel:${emergency.number}`} className="sos-emergency-call-button">
                          <FaPhoneAlt /> Hívás
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Show map and nearby places if type has osmQueries */}
              {loading ? (
                <div className="sos-loading">Közeli helyek keresése...</div>
              ) : SOS_TYPES.find(t => t.id === selectedType)?.osmQueries ? (
                <>
                  {SOS_TYPES.find(t => t.id === selectedType)?.showBothNumbersAndMap && (
                    <div className="sos-section-divider">
                      <h4><FaMapMarkerAlt /> Közeli autómentő szolgáltatók</h4>
                    </div>
                  )}
                  {userLocation && (
                    <div className="sos-map-container">
                      <MapContainer
                        center={[userLocation.lat, userLocation.lng]}
                        zoom={13}
                        style={{ height: '400px', width: '100%', borderRadius: '8px' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <MapController center={[userLocation.lat, userLocation.lng]} />

                        {/* User location marker */}
                        <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                          <Popup>
                            <strong>Te itt vagy</strong>
                          </Popup>
                        </Marker>

                        {/* Service location markers */}
                        {nearbyPlaces.map(place => (
                          <Marker
                            key={place.id}
                            position={[place.lat, place.lng]}
                            icon={serviceIcon}
                          >
                            <Popup>
                              <div className="sos-popup">
                                <strong>{place.name}</strong>
                                <div className="sos-popup-distance"><FaMapMarkerAlt /> {place.distance} km</div>
                                {place.address && (
                                  <div className="sos-popup-address">{place.address}</div>
                                )}
                                {place.phone && (
                                  <div className="sos-popup-phone">
                                    <FaPhoneAlt /> <a href={`tel:${place.phone}`}>{place.phone}</a>
                                  </div>
                                )}
                                {place.website && (
                                  <div className="sos-popup-website">
                                    <FaGlobe /> <a href={place.website} target="_blank" rel="noopener noreferrer">
                                      Weboldal
                                    </a>
                                  </div>
                                )}
                                {place.opening_hours && (
                                  <div className="sos-popup-hours"><FaClock /> {place.opening_hours}</div>
                                )}
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  )}

                  {nearbyPlaces.length > 0 ? (
                    <div className="sos-places-list">
                      <h4>Közeli helyek ({nearbyPlaces.length}):</h4>
                      {nearbyPlaces.map(place => (
                        <div key={place.id} className="sos-place-card">
                          <div className="sos-place-header">
                            <strong>{place.name}</strong>
                            <span className="sos-place-distance">{place.distance} km</span>
                          </div>
                          {place.address && (
                            <div className="sos-place-info"><FaMapMarkerAlt /> {place.address}</div>
                          )}
                          {place.phone && (
                            <div className="sos-place-info">
                              <FaPhoneAlt /> <a href={`tel:${place.phone}`}>{place.phone}</a>
                            </div>
                          )}
                          {place.website && (
                            <div className="sos-place-info">
                              <FaGlobe /> <a href={place.website} target="_blank" rel="noopener noreferrer">
                                Weboldal
                              </a>
                            </div>
                          )}
                          {place.opening_hours && (
                            <div className="sos-place-info"><FaClock /> {place.opening_hours}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="sos-no-results">
                      Nem találtunk közeli helyet. Próbálj meg másik kategóriát választani.
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SOSModal;
